import { useAuth0 } from "@auth0/auth0-react"
import { useFlags as useLDFlags } from "launchdarkly-react-client-sdk"
import { useEffect } from "react"

import { api } from "~/utils/api"
import {
  failHakioFlags,
  receiveHakioFlags,
  useHakioFlagsStore,
} from "~/features/feature-flags/client/hakioFlagsStore"
import { HAKIO_FLAGS_SWITCH } from "~/features/feature-flags/shared/registry"

/** How long the first render waits for the in-house flags before the fallback applies. */
const HAKIO_FLAGS_WAIT_MS = 5_000

const REFRESH_INTERVAL_MS = 60_000

/** Whether the app reads flags from the in-house system (the `hakioFeatureFlags` LaunchDarkly
 * switch is on). For screens that show flag data from one system or the other. */
export const useHakioFlagsEnabled = () => useLDFlags()[HAKIO_FLAGS_SWITCH] === true

/**
 * Loads the in-house flags into `useHakioFlagsStore` and keeps them fresh, whichever way the switch
 * is. Call once, at the app root. True while the app should hold its first render for them: until
 * the first load delivers data, fails or times out. A failure or timeout applies the last-known
 * flags, else the registry defaults; a later response replaces them.
 */
export function useHakioFlagsPending(): boolean {
  const { data, isError } = api.featureFlags.resolved.useQuery(undefined, {
    staleTime: REFRESH_INTERVAL_MS / 2,
    refetchInterval: REFRESH_INTERVAL_MS,
    refetchOnWindowFocus: true,
    // The app waits for the first response: fall back after one retry instead of React Query's
    // default three.
    retry: 1,
  })
  const { user } = useAuth0()
  const orgId: string | undefined = user?.org_id
  const userId = user?.sub
  const waiting = useHakioFlagsStore((state) => state.flags === undefined)

  useEffect(() => {
    if (data) receiveHakioFlags(data, { orgId, userId })
    else if (isError) failHakioFlags({ orgId, userId })
  }, [data, isError, orgId, userId])

  useEffect(() => {
    if (!waiting) return undefined
    const timer = setTimeout(() => failHakioFlags({ orgId, userId }), HAKIO_FLAGS_WAIT_MS)
    return () => clearTimeout(timer)
  }, [waiting, orgId, userId])

  return waiting
}
